import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

import { SettingValueModel, UserSettingModel } from '../../../../models/informate';
import { SelectButtonChangeEvent } from 'primeng/selectbutton';

@Component({
    selector: 'app-default-units',
    templateUrl: './default-units.component.html',
    styleUrls: ['./default-units.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefaultUnitsComponent implements OnChanges, OnInit {
    @Input() form: UntypedFormGroup;

    @Input()
    set dataSource(value: UserSettingModel[]) {
        this._dataSource = value;
    }
    get dataSource(): UserSettingModel[] {
        return this._dataSource;
    }

    defaultAllOption: UserSettingModel;
    defaultUnitOptions: UserSettingModel[];
    defaultUnitMetric: boolean;
    defaultUnitImperial: boolean;
    optValArray: any[] = [];

    private _dataSource: UserSettingModel[];
    private internalDataMap: any[];

    constructor(private cd: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.dataSource.firstChange) return;

        this.initialise();
        this.cd.detectChanges();
    }

    ngOnInit(): void {
        if (!this.dataSource?.length) return;

        this.initialise();
        this.cd.detectChanges();
    }

    initialise(): void {
        //console.log('###--> defaultUnits.component', this.dataSource, this.form)

        this.defaultAllOption = this.dataSource.find((item) => {
            return item.setting.key.toLowerCase() === 'defaultunits';
        });

        this.defaultUnitOptions = this.dataSource.filter((item) => {
            return item.setting.key.toLowerCase() !== 'defaultunits';
        });

        //console.log(this.defaultUnitOptions)

        this.internalDataMap = this.defaultUnitOptions.reduce((prev, { setting, settingValue }) => {
            const key = setting.key;
            const ordinal = settingValue.ordinal;
            prev.push({ key, ordinal });
            return prev;
        }, []);

        this.defaultUnitMetric = this.internalDataMap.every((opt) => opt.ordinal === 0); //this.defaultAllOption.settingValue.value.toLowerCase() === 'metric'
        this.defaultUnitImperial = this.internalDataMap.every((opt) => opt.ordinal === 1); //this.defaultAllOption.settingValue.value.toLowerCase() === 'imperial'

        this.defaultUnitOptions.forEach((x) => {
            this.optValArray.push({ key: x.setting.key, val: this.extractOptions(x.setting.settingValues) });
        });
    }

    trackById(index, item) {
        return item.setting.settingId;
    }

    extractOptions(options: SettingValueModel[]) {
        const result = options.map((item) => ({
            label: item.abbreviation ?? item.value,
            value: item.settingValueId,
        }));

        // this.cd.detectChanges(); // Force change detection to ensure UI updates
        return result;
    }

    getOtionbyKeys(key) {
        return this.optValArray.find((x) => x.key == key).val;
    }

    onRadioChange(selectedValue: string) {
        const option = this.defaultUnitOptions.find((item) => item.setting.settingValues.some((opts) => opts.settingValueId === selectedValue));

        if (!option) return;

        const originalSetting = this.defaultUnitOptions.find((y) => y.setting.key === option.setting.key);
        const ordinalTarget = option.setting.settingValues.find((x) => x.settingValueId === selectedValue)?.ordinal;

        this.updateInternalDataMap(option.setting.key, ordinalTarget);

        this.defaultUnitMetric = this.internalDataMap.every((opt) => opt.ordinal === 0);
        this.defaultUnitImperial = this.internalDataMap.every((opt) => opt.ordinal === 1);

        this.controlStateUpdate(option.setting.key, originalSetting.settingValue.ordinal.toString(), ordinalTarget.toString());
    }

    changeDefault($event: MatSlideToggleChange) {
        const defaultOptionsSettingValues = this.defaultAllOption.setting.settingValues;

        switch ($event.source.name) {
            case 'metric':
                this.defaultUnitMetric = $event.checked;
                this.defaultUnitImperial = !$event.checked;
                this.updateDefaultUnitSetting(
                    defaultOptionsSettingValues.find((option) => option.value.toLowerCase() === ($event.checked ? 'metric' : 'imperial'))
                );
                break;
            case 'imperial':
                this.defaultUnitImperial = $event.checked;
                this.defaultUnitMetric = !$event.checked;
                this.updateDefaultUnitSetting(
                    defaultOptionsSettingValues.find((option) => option.value.toLowerCase() === ($event.checked ? 'imperial' : 'metric'))
                );
                break;
            default:
        }

        this.swtichDefaultStates();
    }

    swtichDefaultStates() {
        const ordinalTarget = this.defaultUnitMetric ? 0 : 1;
        this.defaultUnitOptions.forEach((item) => {
            const key = item.setting.key;
            this.form.get(key).setValue(item.setting.settingValues.find((option) => option.ordinal === ordinalTarget).settingValueId);

            this.controlStateUpdate(key, item.settingValue.ordinal.toString(), ordinalTarget.toString());

            this.updateInternalDataMap(key, ordinalTarget);
        });
    }

    updateDefaultUnitSetting(settingValue: SettingValueModel) {
        const originalSetting = this.defaultAllOption.settingValue;
        this.form.get('DefaultUnits').setValue(settingValue.settingValueId);

        this.controlStateUpdate('DefaultUnits', originalSetting.settingValueId.toLowerCase(), settingValue.settingValueId.toLowerCase());
    }

    updateInternalDataMap(key: string, ordinal: number) {
        const dataMapIdx = this.internalDataMap.findIndex((x) => x.key === key);
        this.internalDataMap[dataMapIdx] = { key, ordinal };
    }

    controlStateUpdate(key: string, originalValue: string, newValue: string): void {
        if (originalValue !== newValue) {
            this.form.get(key).markAsDirty();
            this.form.get(key).markAsTouched();
        } else {
            this.form.get(key).markAsPristine();
        }
    }

    returnFormValue(key) {
        return this.form.get(key).value;
    }
}
